import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  createCertificateAsync,
  fetchStudentsForDropdownAsync,
  clearError,
  type Student,
  type CreateCertificateData
} from '../store/slices/certificatesSlice';
import LoadingSpinner from './LoadingSpinner';
import { getApiErrorMessage } from '../utils/apiError';
import axios from 'axios';
import './CreateCertificate.css';

const CreateCertificate: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { studentsForDropdown, loading, error } = useAppSelector((state) => state.certificates);
  const { token } = useAppSelector((state) => state.auth);

  const [formData, setFormData] = useState({
    studentId: '',
    serviceType: 'Групповые занятия',
    certNumber: '', // Optional certificate number
    lessonsCount: '',
    pricePerLesson: '',
    servicePeriodFrom: '',
    servicePeriodTo: '',
    paymentDate: '',
    bankReferenceNumber: ''
  });

  const [showStudentDropdown, setShowStudentDropdown] = useState(false);
  const [studentSearchQuery, setStudentSearchQuery] = useState('');
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  interface CertNumberValidation {
    isChecking: boolean;
    isValid: boolean | null;
    message: string;
  }

  // Result of the async (API) certificate-number availability check. The
  // synchronous validation states (empty / non-positive number) are derived
  // during render below.
  const [certNumberCheck, setCertNumberCheck] = useState<CertNumberValidation>({
    isChecking: false,
    isValid: null,
    message: ''
  });

  const parsedCertNum = formData.certNumber ? parseInt(formData.certNumber) : NaN;
  const isCertNumSyntacticallyValid =
    !!formData.certNumber && !isNaN(parsedCertNum) && parsedCertNum > 0;

  const certNumberValidation: CertNumberValidation = !formData.certNumber
    ? { isChecking: false, isValid: null, message: '' }
    : !isCertNumSyntacticallyValid
      ? {
          isChecking: false,
          isValid: false,
          message: 'Certificate number must be a positive number'
        }
      : certNumberCheck;

  // Load students when component mounts
  useEffect(() => {
    if (!studentsForDropdown || studentsForDropdown.length === 0) {
      dispatch(fetchStudentsForDropdownAsync());
    }
  }, [studentsForDropdown, dispatch]);



  // Clear any existing errors when component mounts
  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  // Debounced certificate number validation. Only the async availability
  // check lives here; the synchronous validation states are derived during
  // render.
  useEffect(() => {
    if (!isCertNumSyntacticallyValid) {
      return;
    }

    const certNum = parsedCertNum;

    const timeoutId = setTimeout(async () => {
      setCertNumberCheck(prev => ({ ...prev, isChecking: true }));

      try {
        const API_BASE_URL = import.meta.env.VITE_API_URL as string;

        // Get auth headers using Redux token
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        const response = await axios.get(`${API_BASE_URL}/certificates/check-number/${certNum}`, {
          headers
        });

        setCertNumberCheck({
          isChecking: false,
          isValid: !response.data.exists,
          message: response.data.message
        });
      } catch (error) {
        setCertNumberCheck({
          isChecking: false,
          isValid: false,
          message: getApiErrorMessage(error, 'Error checking certificate number')
        });
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [isCertNumSyntacticallyValid, parsedCertNum, token]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.studentId) {
      newErrors.studentId = 'Student is required';
    }
    
    // Validate certificate number if provided
    if (formData.certNumber) {
      const certNum = parseInt(formData.certNumber);
      if (isNaN(certNum) || certNum <= 0) {
        newErrors.certNumber = 'Certificate number must be a positive number';
      } else if (certNumberValidation.isValid === false) {
        newErrors.certNumber = certNumberValidation.message;
      }
    }
    
    if (!formData.lessonsCount || parseInt(formData.lessonsCount) <= 0) {
      newErrors.lessonsCount = 'Lessons count must be greater than 0';
    }
    
    if (!formData.pricePerLesson || parseFloat(formData.pricePerLesson) <= 0) {
      newErrors.pricePerLesson = 'Price per lesson must be greater than 0';
    }
    
    if (!formData.servicePeriodFrom) {
      newErrors.servicePeriodFrom = 'Service start date is required';
    }
    
    if (!formData.servicePeriodTo) {
      newErrors.servicePeriodTo = 'Service end date is required';
    }
    
    if (formData.servicePeriodFrom && formData.servicePeriodTo) {
      if (new Date(formData.servicePeriodTo) <= new Date(formData.servicePeriodFrom)) {
        newErrors.servicePeriodTo = 'End date must be after start date';
      }
    }
    
    if (!formData.paymentDate) {
      newErrors.paymentDate = 'Payment date is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      const certificateData: CreateCertificateData & { certNumber?: number } = {
        studentId: parseInt(formData.studentId),
        serviceType: formData.serviceType as CreateCertificateData['serviceType'],
        lessonsCount: parseInt(formData.lessonsCount),
        pricePerLesson: parseFloat(formData.pricePerLesson),
        servicePeriodFrom: formData.servicePeriodFrom,
        servicePeriodTo: formData.servicePeriodTo,
        paymentDate: formData.paymentDate,
        bankReferenceNumber: formData.bankReferenceNumber
      };
      
      // Add certificate number if provided
      if (formData.certNumber) {
        certificateData.certNumber = parseInt(formData.certNumber);
      }
      
      await dispatch(createCertificateAsync(certificateData)).unwrap();

      // Navigate back to certificates list on success
      navigate('/certificates');
    } catch (error) {
      // Error handling is done in the slice
      console.error('Failed to create certificate:', error);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleStudentSelect = (student: Student) => {
    setSelectedStudent(student);
    setFormData(prev => ({ ...prev, studentId: student.id.toString() }));
    setShowStudentDropdown(false);
    setStudentSearchQuery(student.fullName);
    // Clear student error
    if (errors.studentId) {
      setErrors(prev => ({ ...prev, studentId: '' }));
    }
  };

  const handleStudentSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setStudentSearchQuery(value);
    setShowStudentDropdown(value.length > 0);
    
    if (!value) {
      setSelectedStudent(null);
      setFormData(prev => ({ ...prev, studentId: '' }));
    }
  };

  const filteredStudents = studentsForDropdown?.filter(student =>
    student.fullName.toLowerCase().includes(studentSearchQuery.toLowerCase())
  ) || [];

  const calculateTotal = () => {
    const lessons = parseInt(formData.lessonsCount) || 0;
    const price = parseFloat(formData.pricePerLesson) || 0;
    return (lessons * price).toFixed(2);
  };

  if (loading) {
    return (
      <div className="create-certificate-page">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="create-certificate-page">
      <div className="page-header">
        <h1>Create New Certificate</h1>
        <button 
          onClick={() => navigate('/certificates')} 
          className="btn-secondary"
        >
          ← Back to Certificates
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => dispatch(clearError())} className="close-error">×</button>
        </div>
      )}

      <div className="create-certificate-form">
        <form onSubmit={handleSubmit}>
          <div className="form-grid">
            {/* Student Selection */}
            <div className="form-group full-width">
              <label>Student *</label>
              <div className="student-selector">
                {selectedStudent ? (
                  <div className="selected-student">
                    <span>{selectedStudent.fullName}</span>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedStudent(null);
                        setStudentSearchQuery('');
                        setFormData(prev => ({ ...prev, studentId: '' }));
                      }}
                      className="clear-selection"
                    >
                      ×
                    </button>
                  </div>
                ) : (
                  <>
                    <input
                      type="text"
                      placeholder="Search and select student..."
                      value={studentSearchQuery}
                      onChange={handleStudentSearchChange}
                      onFocus={() => setShowStudentDropdown(studentSearchQuery.length > 0)}
                      className={errors.studentId ? 'error' : ''}
                    />
                    {showStudentDropdown && (
                      <div className="student-dropdown">
                        {filteredStudents.length > 0 ? (
                          filteredStudents.map(student => (
                            <div
                              key={student.id}
                              className="student-option"
                              onClick={() => handleStudentSelect(student)}
                            >
                              {student.fullName}
                            </div>
                          ))
                        ) : (
                          <div className="student-option disabled">
                            No students found
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
              {errors.studentId && <div className="error-text">{errors.studentId}</div>}
            </div>

            {/* Service Type */}
            <div className="form-group">
              <label>Service Type *</label>
              <select
                  className="form-select"
                  value={formData.serviceType}
                  onChange={(e) => handleInputChange('serviceType', e.target.value)}
              >
                <option value="Групповые занятия">Групповые занятия</option>
                <option value="Индивидуальное обучение">Индивидуальное обучение</option>
              </select>
            </div>

            {/* Certificate Number */}
            <div className="form-group">
              <label>Certificate Number</label>
              <div className="input-with-validation">
                <input
                  type="number"
                  min="1"
                  value={formData.certNumber}
                  onChange={(e) => handleInputChange('certNumber', e.target.value)}
                  placeholder="Leave empty for auto-generation"
                  className={`${errors.certNumber ? 'error' : ''} ${formData.certNumber && certNumberValidation.isValid === true ? 'valid-input' : ''} ${formData.certNumber && certNumberValidation.isValid === false ? 'invalid-input' : ''}`}
                />
                {formData.certNumber && (
                  <div className="validation-indicator">
                    {certNumberValidation.isChecking ? (
                      <span className="checking small-icon">Checking...</span>
                    ) : certNumberValidation.isValid === true ? (
                      <span className="valid small-icon">Available</span>
                    ) : certNumberValidation.isValid === false ? (
                      <span className="invalid small-icon">{certNumberValidation.message}</span>
                    ) : null}
                  </div>
                )}
              </div>
              {errors.certNumber && <div className="error-text">{errors.certNumber}</div>}
            </div>

            {/* Lessons Count */}
            <div className="form-group">
              <label>Lessons Count *</label>
              <input
                type="number"
                min="1"
                value={formData.lessonsCount}
                onChange={(e) => handleInputChange('lessonsCount', e.target.value)}
                className={errors.lessonsCount ? 'error' : ''}
              />
              {errors.lessonsCount && <div className="error-text">{errors.lessonsCount}</div>}
            </div>

            {/* Price per Lesson */}
            <div className="form-group">
              <label>Price per Lesson (BYN) *</label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                value={formData.pricePerLesson}
                onChange={(e) => handleInputChange('pricePerLesson', e.target.value)}
                className={errors.pricePerLesson ? 'error' : ''}
              />
              {errors.pricePerLesson && <div className="error-text">{errors.pricePerLesson}</div>}
            </div>

            {/* Total Amount Display */}
            <div className="form-group">
              <label>Total Amount (BYN)</label>
              <div className="total-amount-display">
                {calculateTotal()} BYN
              </div>
            </div>

            {/* Service Period From */}
            <div className="form-group">
              <label>Service Period From *</label>
              <input
                type="date"
                value={formData.servicePeriodFrom}
                onChange={(e) => handleInputChange('servicePeriodFrom', e.target.value)}
                className={errors.servicePeriodFrom ? 'error' : ''}
              />
              {errors.servicePeriodFrom && <div className="error-text">{errors.servicePeriodFrom}</div>}
            </div>

            {/* Service Period To */}
            <div className="form-group">
              <label>Service Period To *</label>
              <input
                type="date"
                value={formData.servicePeriodTo}
                onChange={(e) => handleInputChange('servicePeriodTo', e.target.value)}
                className={errors.servicePeriodTo ? 'error' : ''}
              />
              {errors.servicePeriodTo && <div className="error-text">{errors.servicePeriodTo}</div>}
            </div>

            {/* Payment Date */}
            <div className="form-group">
              <label>Payment Date *</label>
              <input
                type="date"
                value={formData.paymentDate}
                onChange={(e) => handleInputChange('paymentDate', e.target.value)}
                className={errors.paymentDate ? 'error' : ''}
              />
              {errors.paymentDate && <div className="error-text">{errors.paymentDate}</div>}
            </div>

            {/* Bank Reference Number */}
            <div className="form-group">
              <label>Bank Reference Number</label>
              <input
                type="text"
                value={formData.bankReferenceNumber}
                onChange={(e) => handleInputChange('bankReferenceNumber', e.target.value)}
                placeholder="Enter bank reference number"
                className={errors.bankReferenceNumber ? 'error' : ''}
              />
              {errors.bankReferenceNumber && <div className="error-text">{errors.bankReferenceNumber}</div>}
            </div>
          </div>

          <div className="form-actions">
            <button 
              type="button" 
              onClick={() => navigate('/certificates')}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn-success"
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Certificate'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateCertificate; 